<template>
  <div id="app" :class="{ 'hide-mobile-menu': hideMenu || miniMenu }" :data-theme="!(hideMenu || miniMenu) ? (themeStore.isDark ? 'dark' : 'light') : undefined">
    <div :class="['fixed-top w-100 z-index-menu']">
      <MenuMobile />
    </div>

    <div class="container-app p-0 h-100">
      <div class="row-app h-100 w-100 m-0">
        <div v-if="!hideMenu" class="sidebar-desktop-fixed d-none d-md-flex p-0 m-0">
          <Menu :force-collapsed="miniMenu" />
        </div>

        <div class="main-content-area p-0 d-flex flex-column bg-gray-custom">
          <main class="flex-grow-1 overflow-y-auto -webkit-overflow-scrolling-touch">
            <div class="router-view-content-wrapper">
              <router-view />
            </div>
          </main>
        </div>
      </div>
    </div>

    <FullScreenAlert
      v-if="alertStore.visible"
      :loading="alertStore.isLoading"
      :title="alertStore.title"
      :message="alertStore.message"
      @close="alertStore.close()"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAlertStore } from './stores/alertStore';
import { useEventStore } from './stores/eventStore';
import { useThemeStore } from './stores/themeStore';

// Importe seus componentes
import MenuMobile from './components/MenuMobile.vue';
import Menu from './components/Menu.vue'; // Sua sidebar desktop
import FullScreenAlert from './components/FullScreenAlert.vue';

const alertStore = useAlertStore();
const eventStore = useEventStore();
const themeStore = useThemeStore();
const route = useRoute();

const hideMenuRoutes = ['PaymentView', 'Checkout', 'EventDetails', 'Cart'];
const miniMenuRoutes = ['EventEdit', 'EventCreate'];
const hideMenu = computed(() =>
  hideMenuRoutes.includes(route.name) || eventStore.whiteLabel
);
const miniMenu = computed(() =>
  miniMenuRoutes.includes(route.name) || route.path === '/my-account/add'
);
</script>

<style>
/* CSS GLOBAL NO APP.VUE */
:root {
  --primary-color: #f78da7;
  --secondary-color: #32355d;
  --text-muted-color: #8f9093;
  --border-line-color: #ececec;
  --input-bg-soft: #fcfcfd;

  /* Panel light defaults */
  --panel-bg: #f8f9fa;
  --panel-surface: #ffffff;
  --panel-text: #1f2937;
  --panel-text-muted: #6b7280;
  --panel-border: #e5e7eb;
  --panel-sidebar-bg: #ffffff;
  --panel-sidebar-text: #32355d;
  --panel-sidebar-muted: #8f9093;
  --panel-nav-active-bg: #fff0f6;
  --panel-nav-active-text: #f78da7;
  --panel-card-bg: #ffffff;
  --panel-card-border: #e5e7eb;
  --panel-scrollbar-track: #f8f9fa;
  --panel-scrollbar-thumb: #bbb;
}

[data-theme="dark"] {
  --panel-bg: #0f1117;
  --panel-surface: #1a1d27;
  --panel-text: #e9ecef;
  --panel-text-muted: #9ca3af;
  --panel-border: #2d3250;
  --panel-sidebar-bg: #14172b;
  --panel-sidebar-text: #e9ecef;
  --panel-sidebar-muted: #9ca3af;
  --panel-nav-active-bg: rgba(247, 141, 167, 0.15);
  --panel-nav-active-text: #f78da7;
  --panel-card-bg: #1a1d27;
  --panel-card-border: #2d3250;
  --panel-scrollbar-track: #1a1d27;
  --panel-scrollbar-thumb: #3d4268;
}

html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden; /* CRÍTICO: Impede rolagem do documento principal */
}

#app {
  height: 100%;
  font-family: 'Baloo 2', cursive !important;
  display: flex;
  flex-direction: column;
}

.container-app {
  height: 100%;
  display: flex;
  flex-grow: 1;
  position: relative;
  z-index: 1029; /* Abaixo do MenuMobile no mobile, mas ainda acima de outros conteúdos padrão */
}

.row-app {
  height: 100%;
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  margin: 0;
  padding: 0;
}

/* Z-index para a div pai do MenuMobile */
.z-index-menu {
  z-index: 1030; /* Garante que o MenuMobile esteja no topo no mobile */
  display: none; /* Inicia escondido por padrão, será sobrescrito em mobile */
}

.sidebar-desktop-fixed {
  width: 270px;
  flex-shrink: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease;
}

.sidebar-desktop-fixed:has(.sidebar--collapsed) {
  width: 64px;
}

.main-content-area {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.main-content-area main {
  flex-grow: 1;
  overflow-y: auto; /* AQUI ESTÁ A ROLAGEM PRINCIPAL DO CONTEÚDO */
  -webkit-overflow-scrolling: touch;
  position: relative;
  padding-top: 0; /* Padrão, será sobrescrito em mobile */
}

/* --- AJUSTES ESPECÍFICOS PARA DISPOSITIVOS MÓVEIS (Telhas menores que 768px) --- */
@media (max-width: 767.98px) {
  /* CRÍTICO: MenuMobile SEMPRE visível em mobile */
  .z-index-menu {
    display: flex !important; /* Força a exibição do MenuMobile em mobile */
  }

  /* Esconde menu mobile em telas de checkout/pagamento */
  .hide-mobile-menu .z-index-menu {
    display: none !important;
  }
  .hide-mobile-menu .main-content-area {
    margin-top: 0;
  }

  .main-content-area{
    margin-top: 60px;
    z-index: 1051;   
  }

  .main-content-area main {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-top: 1rem;
  }

  .row-app {
    width: 100%;
  }
}

/* Garante que o iOS não dê zoom ao focar em campos de formulário */
@media screen and (max-width: 768px) {
  input, 
  select, 
  textarea {
    font-size: 16px !important;
  }
}

/* --- AJUSTES PARA DESKTOP (Telhas maiores ou iguais a 768px) --- */
@media (min-width: 768px) {
  /* CRÍTICO: MenuMobile SEMPRE escondido em desktop */
  .z-index-menu {
    display: none !important;
  }

  .container-app {
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    border-left: 1px solid #ddd;
    border-right: 1px solid #ddd;
    margin: auto;
    width: 100%;
    max-width: 100%;
    z-index: 1; /* Valor baixo, pois o MenuMobile fixo não estará visível */
  }

  .row-app {
    width: 100%;
    height: 100%;
  }

  /* Garante que não haja padding-top extra no desktop */
  .main-content-area main {
    padding-top: 0;
    padding-bottom: 0;
  }
}

.router-view-content-wrapper {
  height: 100%;
  width: 100%;
}

.router-view-content-wrapper > * {
  height: 100%;
}

.bg-gray-custom {
  background-color: var(--panel-bg, #f8f9fa);
}

/* Estilos de Scrollbar */
.main-content-area main::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.main-content-area main::-webkit-scrollbar-track {
  background-color: var(--panel-scrollbar-track, #f8f9fa);
  border-radius: 10px;
}
.main-content-area main::-webkit-scrollbar-thumb {
  background-color: var(--panel-scrollbar-thumb, #bbb);
  border-radius: 10px;
}
.main-content-area main::-webkit-scrollbar-thumb:hover {
  background-color: var(--panel-scrollbar-thumb, #bbb);
  opacity: 0.8;
}

/* Panel sidebar dark mode surface */
.sidebar-desktop-fixed {
  background-color: var(--panel-sidebar-bg, #ffffff);
}

/* Panel main area dark mode surface */
[data-theme="dark"] .main-content-area.bg-gray-custom,
[data-theme="dark"] .main-content-area {
  background-color: var(--panel-bg);
}



.tooltip {
  z-index: 999999 !important;
}

/* TOAST — pílula escura centralizada na base */
.Toastify__toast-container--bottom-center {
  left: 50% !important;
  bottom: 1.25rem !important;
  transform: translateX(-50%) !important;
  width: auto !important;
  max-width: 92vw !important;
  padding: 0 !important;
}

.Toastify__toast {
  background: #1a1a2e !important;
  color: #fff !important;
  border-radius: 99px !important;
  padding: 0.5rem 1.1rem !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  box-shadow: 0 4px 16px rgba(0,0,0,0.22) !important;
  min-height: unset !important;
  margin-bottom: 0 !important;
  line-height: 1.4 !important;
}

.Toastify__toast-body {
  padding: 0 !important;
  margin: 0 !important;
}

.Toastify__toast-icon { display: none !important; }
.Toastify__close-button { display: none !important; }
.Toastify__progress-bar { display: none !important; }

.Toastify__toast--success::before {
  content: '✓  ';
  font-weight: 700;
  color: #4ade80;
}

.Toastify__toast--error::before {
  content: '✕  ';
  font-weight: 700;
  color: #f87171;
}

.Toastify__toast--warning::before {
  content: '⚠  ';
  font-weight: 700;
  color: #fbbf24;
}

@media only screen and (max-width: 480px) {
  .Toastify__toast-container--bottom-center {
    bottom: 1rem !important;
    max-width: 92vw !important;
  }
  .Toastify__toast {
    white-space: normal !important;
  }
}

/* ══════════════════════════════════════════════════════════
   DARK MODE — Overrides globais do painel
   Cobre Bootstrap utilities + forms + tables + modais
   Não afeta rotas de convidado (sem data-theme no #app)
   ══════════════════════════════════════════════════════════ */

/* Texto geral */
[data-theme="dark"] {
  color: var(--panel-text);
}

/* Headings */
[data-theme="dark"] h1,
[data-theme="dark"] h2,
[data-theme="dark"] h3,
[data-theme="dark"] h4,
[data-theme="dark"] h5,
[data-theme="dark"] h6 {
  color: var(--panel-text);
}

/* Bootstrap text utilities */
[data-theme="dark"] .text-muted    { color: var(--panel-text-muted) !important; }
[data-theme="dark"] .text-dark     { color: var(--panel-text) !important; }
[data-theme="dark"] .text-secondary { color: var(--panel-text-muted) !important; }
[data-theme="dark"] .text-body     { color: var(--panel-text) !important; }
[data-theme="dark"] small          { color: var(--panel-text-muted); }

/* Bootstrap background utilities */
[data-theme="dark"] .bg-white  { background-color: var(--panel-card-bg) !important; }
[data-theme="dark"] .bg-light  { background-color: var(--panel-surface) !important; }
[data-theme="dark"] .bg-body   { background-color: var(--panel-bg) !important; }

/* Bootstrap border utilities */
[data-theme="dark"] .border,
[data-theme="dark"] .border-top,
[data-theme="dark"] .border-bottom,
[data-theme="dark"] .border-start,
[data-theme="dark"] .border-end    { border-color: var(--panel-border) !important; }

/* Cards Bootstrap */
[data-theme="dark"] .card {
  background-color: var(--panel-card-bg);
  border-color: var(--panel-card-border);
  color: var(--panel-text);
}
[data-theme="dark"] .card-header,
[data-theme="dark"] .card-footer {
  background-color: var(--panel-surface);
  border-color: var(--panel-border);
}
[data-theme="dark"] .card-title { color: var(--panel-text); }
[data-theme="dark"] .card-text  { color: var(--panel-text-muted); }

/* Inputs, selects, textareas */
[data-theme="dark"] .form-control,
[data-theme="dark"] .form-select,
[data-theme="dark"] .form-control:focus,
[data-theme="dark"] .form-select:focus {
  background-color: var(--panel-surface) !important;
  color: var(--panel-text) !important;
  border-color: var(--panel-border) !important;
}
[data-theme="dark"] .form-control::placeholder,
[data-theme="dark"] .form-select::placeholder {
  color: var(--panel-text-muted);
}
[data-theme="dark"] .input-group-text {
  background-color: var(--panel-border);
  color: var(--panel-text-muted);
  border-color: var(--panel-border);
}
[data-theme="dark"] .form-label { color: var(--panel-text); }
[data-theme="dark"] .form-text  { color: var(--panel-text-muted); }

/* Inputs plain (não Bootstrap) */
[data-theme="dark"] input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([class*="btn"]),
[data-theme="dark"] select:not([class*="btn"]),
[data-theme="dark"] textarea {
  background-color: var(--panel-surface) !important;
  color: var(--panel-text) !important;
  border-color: var(--panel-border) !important;
}
[data-theme="dark"] input::placeholder,
[data-theme="dark"] textarea::placeholder { color: var(--panel-text-muted); }

/* Tables */
[data-theme="dark"] .table {
  color: var(--panel-text);
  border-color: var(--panel-border);
  --bs-table-bg: var(--panel-card-bg);
  --bs-table-striped-bg: var(--panel-surface);
  --bs-table-hover-bg: var(--panel-surface);
  --bs-table-border-color: var(--panel-border);
}
[data-theme="dark"] .table th,
[data-theme="dark"] .table td  { border-color: var(--panel-border); }
[data-theme="dark"] .table thead th { color: var(--panel-text-muted); background-color: var(--panel-surface); }

/* Tables sem classe Bootstrap (custom) */
[data-theme="dark"] table { color: var(--panel-text); border-color: var(--panel-border); }
[data-theme="dark"] th    { color: var(--panel-text-muted); background-color: var(--panel-surface); border-color: var(--panel-border) !important; }
[data-theme="dark"] td    { border-color: var(--panel-border) !important; }
[data-theme="dark"] tr:hover { background-color: var(--panel-surface); }

/* Modais */
[data-theme="dark"] .modal-content {
  background-color: var(--panel-surface);
  border-color: var(--panel-border);
  color: var(--panel-text);
}
[data-theme="dark"] .modal-header,
[data-theme="dark"] .modal-footer { border-color: var(--panel-border); }
[data-theme="dark"] .modal-title  { color: var(--panel-text); }
[data-theme="dark"] .modal-backdrop { opacity: 0.7; }

/* Dropdowns */
[data-theme="dark"] .dropdown-menu {
  background-color: var(--panel-surface);
  border-color: var(--panel-border);
}
[data-theme="dark"] .dropdown-item          { color: var(--panel-text); }
[data-theme="dark"] .dropdown-item:hover,
[data-theme="dark"] .dropdown-item:focus    { background-color: var(--panel-nav-active-bg); color: var(--panel-nav-active-text); }
[data-theme="dark"] .dropdown-divider       { border-color: var(--panel-border); }

/* Botões outline */
[data-theme="dark"] .btn-outline-secondary {
  color: var(--panel-text-muted);
  border-color: var(--panel-border);
}
[data-theme="dark"] .btn-outline-secondary:hover {
  background-color: var(--panel-surface);
  color: var(--panel-text);
  border-color: var(--panel-text-muted);
}
[data-theme="dark"] .btn-light {
  background-color: var(--panel-surface);
  border-color: var(--panel-border);
  color: var(--panel-text);
}

/* Badges neutros */
[data-theme="dark"] .badge.bg-secondary,
[data-theme="dark"] .badge.bg-light { background-color: var(--panel-border) !important; color: var(--panel-text-muted) !important; }

/* Nav tabs */
[data-theme="dark"] .nav-tabs         { border-color: var(--panel-border); }
[data-theme="dark"] .nav-tabs .nav-link        { color: var(--panel-text-muted); }
[data-theme="dark"] .nav-tabs .nav-link:hover  { color: var(--panel-text); border-color: var(--panel-border); }
[data-theme="dark"] .nav-tabs .nav-link.active {
  color: var(--panel-text);
  background-color: var(--panel-surface);
  border-color: var(--panel-border) var(--panel-border) var(--panel-surface);
}

/* Paginação */
[data-theme="dark"] .page-link {
  background-color: var(--panel-surface);
  border-color: var(--panel-border);
  color: var(--panel-text);
}
[data-theme="dark"] .page-item.active .page-link {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}
[data-theme="dark"] .page-item.disabled .page-link { color: var(--panel-text-muted); }

/* Spinner / loading genérico */
[data-theme="dark"] .spinner-border { color: var(--panel-text-muted); }

/* Divider hr */
[data-theme="dark"] hr { border-color: var(--panel-border); opacity: 1; }

/* List groups */
[data-theme="dark"] .list-group-item {
  background-color: var(--panel-card-bg);
  border-color: var(--panel-border);
  color: var(--panel-text);
}
[data-theme="dark"] .list-group-item:hover { background-color: var(--panel-surface); }

/* Alerts Bootstrap */
[data-theme="dark"] .alert-info    { background-color: #1e3a5f; border-color: #2563eb33; color: #93c5fd; }
[data-theme="dark"] .alert-warning { background-color: #3b2f0a; border-color: #d9770633; color: #fcd34d; }
[data-theme="dark"] .alert-danger  { background-color: #3b1a1a; border-color: #dc262633; color: #fca5a5; }
[data-theme="dark"] .alert-success { background-color: #0a2e1e; border-color: #05966933; color: #6ee7b7; }

</style>